import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArticleCard } from "@/components/ArticleCard";
import { SocialProofToast } from "@/components/SocialProofToast";
import { Dashboard } from "@/components/Dashboard";
import { useAuth } from "@/hooks/useAuth";
import { blogPosts } from "@/data/blogData";
import { Link } from "react-router-dom";
import { ArrowRight, LayoutDashboard } from "lucide-react";
import heroStadium from "@/assets/hero-stadium.jpg";
import heroStadiumWebP from "@/assets/hero-stadium.webp";
import heroGroups from "@/assets/groups.png";
import heroGroupsWebP from "@/assets/groups.webp";
import bracketsClean from "@/assets/brackets_clean.png";
import bracketsCleanWebP from "@/assets/brackets_clean.webp";
import heroDribble from "@/assets/blog-dribble.jpg";
import heroDribbleWebP from "@/assets/blog-dribble.webp";
// Homepage tiles use dedicated branded composites (baked-in typography +
// visual catalog) for stronger conversion than stock photos.
import tournamentsImage from "@/assets/tournaments.png";
import tournamentsImageWebP from "@/assets/tournaments.webp";
import prizesImage from "@/assets/prizes.png";
import prizesImageWebP from "@/assets/prizes.webp";
import heroCelebration from "@/assets/blog-celebration.jpg";
import heroCelebrationWebP from "@/assets/blog-celebration.webp";
import { Picture } from "@/components/Picture";

const ease = [0.2, 0.8, 0.2, 1] as [number, number, number, number];

/** Circular tournament badge with graceful fallback when the logo URL
 *  404s or fails to load — falls back to the tournament emoji. */
const Index = () => {
  const { user } = useAuth();

  useEffect(() => {
    document.title = "FC Bracket — Bracket Challenges for Every Major Soccer Tournament";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute(
      "content",
      "Play free bracket challenges for the World Cup, Champions League, Europa League, MLS Cup, and more. Compete with friends, win prizes, track your record."
    );
  }, []);

  // ── Logged-in: show the dashboard hub ──
  if (user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
              <LayoutDashboard className="w-6 h-6 md:w-7 md:h-7 text-primary shrink-0" />
              <span>Welcome back{user?.email ? `, ${user.email.split("@")[0]}` : ""}</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Your tournaments, picks, and standings — all in one place.
            </p>
          </motion.div>
          <Dashboard />
        </div>
      </div>
    );
  }

  return (
    <div className="theme-nike min-h-screen bg-background text-foreground overflow-x-hidden">
      <Header />

      {/* ── HERO — full-bleed stadium, volt CTA ── */}
      <section className="relative h-[75vh] min-h-[560px] overflow-hidden">
        <Picture src={heroGroups} webp={heroGroupsWebP} alt="" loading="eager" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />

        <div className="relative h-full container mx-auto px-6 flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="max-w-3xl"
          >
            <p className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-primary mb-5">
              9 tournaments · Free to play · Win prizes
            </p>
            <h1 className="font-display text-5xl sm:text-7xl md:text-8xl font-black text-white leading-[0.9] mb-6 tracking-tight">
              BRACKETS<br />
              FOR EVERY<br />
              <span className="text-primary">MATCH.</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-white/80 max-w-xl mb-8">
              Play the World Cup, Champions League, MLS Cup & more. Free to enter. Real prizes on the line.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/auth"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display font-black text-base uppercase tracking-wider px-8 h-14 hover:brightness-110 transition-all"
              >
                Play Free <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/world-cup-bracket"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white font-display font-bold text-base uppercase tracking-wider px-8 h-14 hover:bg-white/20 transition-all"
              >
                World Cup 2026
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── START YOUR BRACKET — two big photo tiles ── */}
      <section className="bg-black">
        <div className="container mx-auto px-6 py-12 md:py-16">
          <p className="font-display text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-white mb-6">
            Start Your Bracket
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Link to="/tournaments" className="group relative block aspect-[4/5] overflow-hidden">
              <Picture
                src={tournamentsImage}
                webp={tournamentsImageWebP}
                alt="Browse all 9 tournaments — World Cup, Champions League, Europa, Copa Libertadores, and more."
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {/* Short bottom-fade so the CTA pill stays legible */}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <span className="inline-block bg-primary text-black font-display text-xs font-bold uppercase tracking-wider px-4 h-9 leading-9">Browse Tournaments</span>
              </div>
            </Link>
            <Link to="/lp/world-cup" className="group relative block aspect-[4/5] overflow-hidden">
              <Picture
                src={prizesImage}
                webp={prizesImageWebP}
                alt="Real prizes. Real passion. Win epic prizes for predicting — signed jerseys, match tickets, trips, gift cards, PlayStation, and more."
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {/* Short bottom-fade so the CTA pill stays legible on the busy floor */}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <span className="inline-block bg-primary text-black font-display text-xs font-bold uppercase tracking-wider px-4 h-9 leading-9">
                  Play For Real Prizes
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ── BY THE NUMBERS — centered stats bar with count-up ── */}
      <section className="bg-black border-t border-white/10">
        <div className="container mx-auto px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {([
              { to: 9,     format: (n: number) => String(n),                       label: "Tournaments" },
              { to: 20,    format: (n: number) => `$${n}K+`,                       label: "In Prizes" },
              { to: 180,   format: (n: number) => `${n}+`,                         label: "Countries" },
              { to: 30,    format: (n: number) => `${n}s`,                         label: "Live Scoring" },
            ] as const).map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.05 * i }}
                className="flex flex-col items-center text-center"
              >
                <span className="font-display text-4xl md:text-5xl font-black text-primary leading-none tracking-tight tabular-nums">
                  <CountUp to={stat.to} format={stat.format} />
                </span>
                <span className="font-display text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/60 mt-2">
                  {stat.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BIG CTA (JUST PLAY) — brackets_clean.png background ── */}
      <section className="relative overflow-hidden bg-black py-20 md:py-32 border-t border-white/10">
        {/* Background bracket graphic — full opacity, then a focused
            radial dark behind the copy block only (keeps the edges visible). */}
        <Picture
          src={bracketsClean}
          webp={bracketsCleanWebP}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
        {/* Darker overall — keep the bracket art readable at the edges
            but pull the midtones down so white text + volt accent carry. */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.65)_45%,rgba(0,0,0,0.55)_100%)] pointer-events-none" />
        <div className="container mx-auto px-6 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
          >
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Free to Play</p>
            <h2 className="font-display text-5xl sm:text-7xl md:text-8xl font-black text-white leading-[0.9] tracking-tight mb-6">
              JUST <span className="text-primary">PLAY.</span>
            </h2>
            <p className="font-body text-lg md:text-xl text-white/70 max-w-xl mx-auto mb-8">
              Sign up in under a minute. Pick your winners. Win real prizes.
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display font-black text-base uppercase tracking-wider px-10 h-14 hover:brightness-110 transition-all"
            >
              Create Free Account <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="font-body text-xs text-white/50 mt-4">No credit card required.</p>
          </motion.div>
        </div>
      </section>

      {/* ── Blog (keep, dark theme) ── */}
      <section className="bg-black border-t border-white/10 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-white/60 mb-1">From The</p>
              <h2 className="font-display text-3xl md:text-5xl font-black text-white tracking-tight">BLOG</h2>
            </div>
            <Link to="/blog" className="hidden md:inline-flex items-center gap-2 font-body text-xs font-bold uppercase tracking-wider text-white hover:text-primary transition-colors">
              All Articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((post, i) => (
              <ArticleCard key={post.slug} post={post} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <SocialProofToast />
    </div>
  );
};


export default Index;

// ── CountUp: animate an integer from 0 → `to` when scrolled into view ──
// Runs once per section view (once: true). Uses rAF for smooth 60fps
// integer stepping; eases out cubic so the final value lands softly.
function CountUp({ to, format, duration = 1600 }: { to: number; format: (n: number) => string; duration?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let startTs: number | null = null;
    let raf = 0;
    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const t = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return <span ref={ref}>{format(value)}</span>;
}
